import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useCoreDispatch } from '../../store/CoreStore';
import { betActions } from '../../store/slices/bets/slice';
import { useGameStateSlice } from '../../store/slices/game/selectors';

import type { DragAndDropOriginConnectedArgs } from './types';

export const useDragAndDropOriginConnected: (args: DragAndDropOriginConnectedArgs) => RefObject<HTMLElement> = ({
  value,
  betSpot,
  ref,
}) => {
  const dispatch = useCoreDispatch();
  const originLocalRef = useRef<HTMLElement>(null);
  const originRef = ref || originLocalRef;
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (!isBetsOpenStatus) return;

      const target = event.target as HTMLElement | null;

      if (target && target.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId);
      }

      if (originRef.current) {
        dispatch(
          betActions.setDragBuffer({
            value,
            from: betSpot,
            elementData: {
              origin: {
                x: event.clientX,
                y: event.clientY,
              },
              offset: {
                diffX: 0,
                diffY: 0,
              },
              width: originRef.current.getBoundingClientRect().width,
            },
          })
        );
      }
    },
    [betSpot, dispatch, isBetsOpenStatus, originRef, value]
  );

  useEffect(() => {
    const originElement = originRef.current;
    originElement?.addEventListener('pointerdown', handlePointerDown, { passive: false });

    return (): void => {
      originElement?.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [handlePointerDown, originRef]);

  return originRef;
};
